import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { clampCurrency, normalizeCurrency } from '../components/discountMath';
import type { PosCustomerBalanceSummary, PosPaymentSplit, PosPaymentSummary } from '../types';
import { usePosPaymentMethods, type PosPaymentMethod } from '../api/usePosPaymentMethods';

interface PaymentSplitDraft extends PosPaymentSplit {
  id: string;
}

export type PaymentWarningType = 'underpayment' | 'overpayment' | 'debt';

export interface PaymentWarning {
  type: PaymentWarningType;
  amount: number;
}

export interface PosPaymentsTotals {
  totalTendered: number;
  outstanding: number;
  changeDue: number;
  outstandingBeforeCredit: number;
}

export interface PosPaymentsChange {
  summary: PosPaymentSummary;
  totals: PosPaymentsTotals;
  warnings: PaymentWarning[];
  splits: PaymentSplitDraft[];
  creditAmount: number;
  availableCredit: number;
  isDebtOnly: boolean;
  balanceApplied: number;
}

export interface UsePosPaymentsOptions {
  totalDue: number;
  currency?: string;
  balance?: PosCustomerBalanceSummary | null;
  onPaymentsChange?: (change: PosPaymentsChange) => void;
  externalCreditAmount?: number | null;
  balanceAdjustment?: number;
}

export interface UsePosPaymentsResult {
  paymentMethods: PosPaymentMethod[];
  isLoading: boolean;
  splits: PaymentSplitDraft[];
  addSplit: (methodId?: number | null) => void;
  removeSplit: (id: string) => void;
  updateSplitMethod: (id: string, methodId: number | null) => void;
  updateSplitAmount: (id: string, amount: number) => void;
  fillSplitWithRemaining: (id: string) => void;
  assignTotalToPrimary: () => void;
  computeOutstandingForSplit: (id: string) => number;
  creditAmount: number;
  creditDraft: string;
  setCreditDraft: (value: string) => void;
  applyCreditAmount: (amount: number) => void;
  useMaxCredit: () => void;
  resetCredit: () => void;
  availableCredit: number;
  isDebtOnly: boolean;
  setDebtOnly: (value: boolean) => void;
  debtNote: string;
  setDebtNote: (value: string) => void;
  totals: PosPaymentsTotals;
  warnings: PaymentWarning[];
  summary: PosPaymentSummary;
  buildSummary: () => PosPaymentSummary;
  reset: () => void;
  isCreditReadOnly: boolean;
}

const buildSplit = (id: string, methodId: number | null, amount = 0): PaymentSplitDraft => ({
  id,
  methodId,
  amount,
});

const calculateOutstandingForSplit = (
  targetId: string | null,
  splits: PaymentSplitDraft[],
  totalDue: number,
  creditAmount: number,
): number => {
  const otherTotal = splits.reduce((accumulator, split) => {
    if (targetId !== null && split.id === targetId) {
      return accumulator;
    }
    const sanitized = clampCurrency(Math.max(split.amount, 0));
    return accumulator + sanitized;
  }, 0);
  const outstanding = totalDue - creditAmount - otherTotal;
  return clampCurrency(Math.max(outstanding, 0));
};

export const usePosPayments = ({
  totalDue,
  balance = null,
  onPaymentsChange,
  externalCreditAmount = null,
  balanceAdjustment,
}: UsePosPaymentsOptions): UsePosPaymentsResult => {
  const { data: paymentMethods = [], isLoading } = usePosPaymentMethods();
  const defaultMethodId = paymentMethods[0]?.id ?? null;
  const availableCredit = Number(balance?.available_credit ?? 0) || 0;

  const creditCap = clampCurrency(Math.max(0, Math.min(availableCredit, Math.max(totalDue, 0))));
  const isCreditReadOnly = externalCreditAmount !== null && externalCreditAmount !== undefined;

  const nextIdRef = useRef(1);
  const [splits, setSplits] = useState<PaymentSplitDraft[]>([]);
  const [creditAmount, setCreditAmount] = useState(0);
  const [creditDraft, setCreditDraft] = useState('');
  const [creditManuallySet, setCreditManuallySet] = useState(false);
  const [isDebtOnly, setDebtOnly] = useState(false);
  const [debtNote, setDebtNote] = useState('');

  const commitCredit = useCallback(
    (amount: number, manual: boolean) => {
      if (isCreditReadOnly) return;
      const safeAmount = clampCurrency(Math.max(0, Math.min(amount, creditCap)));
      setCreditAmount(safeAmount);
      setCreditDraft(safeAmount > 0 ? safeAmount.toFixed(2) : '');
      setCreditManuallySet(manual);
    },
    [creditCap, isCreditReadOnly],
  );

  const applyCreditAmount = useCallback(
    (amount: number) => {
      if (isCreditReadOnly) {
        return;
      }
      if (isDebtOnly) {
        commitCredit(0, true);
        return;
      }
      const safeAmount = clampCurrency(Math.max(0, Math.min(amount, creditCap)));
      commitCredit(safeAmount, true);
    },
    [creditCap, commitCredit, isCreditReadOnly, isDebtOnly],
  );

  const setCreditDraftValue = useCallback(
    (value: string) => {
      if (isCreditReadOnly) {
        return;
      }
      setCreditDraft(value);
      const parsed = Number(value.replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed < 0) {
        commitCredit(0, true);
        return;
      }
      applyCreditAmount(parsed);
    },
    [applyCreditAmount, commitCredit, isCreditReadOnly],
  );

  const resetCredit = useCallback(() => {
    if (isCreditReadOnly) {
      return;
    }
    commitCredit(0, true);
  }, [commitCredit, isCreditReadOnly]);

  useEffect(() => {
    if (isCreditReadOnly || isDebtOnly) {
      return;
    }
    const safeTotal = clampCurrency(Math.max(totalDue, 0));
    const maxAllowed = clampCurrency(Math.min(availableCredit, safeTotal));

    if (creditManuallySet) {
      if (creditAmount > maxAllowed + 0.009) {
        commitCredit(maxAllowed, true);
      }
      return;
    }

    if (Math.abs(creditAmount - maxAllowed) > 0.009) {
      commitCredit(maxAllowed, false);
    }
  }, [
    availableCredit,
    commitCredit,
    creditAmount,
    creditManuallySet,
    isCreditReadOnly,
    isDebtOnly,
    totalDue,
  ]);

  useEffect(() => {
    if (isDebtOnly) {
      if (isCreditReadOnly) {
        setCreditAmount(0);
        setCreditDraft('');
      } else {
        commitCredit(0, false);
      }
      return;
    }
  }, [commitCredit, isCreditReadOnly, isDebtOnly]);

  useEffect(() => {
    setCreditManuallySet(false);
  }, [balance?.customer_id]);

  useEffect(() => {
    if (!isCreditReadOnly) {
      return;
    }
    const desired = clampCurrency(Math.max(0, Math.min(externalCreditAmount ?? 0, creditCap)));
    if (Math.abs(desired - creditAmount) > 0.01) {
      setCreditAmount(desired);
    }
    setCreditDraft(desired > 0 ? desired.toFixed(2) : '');
    setCreditManuallySet(false);
  }, [creditAmount, creditCap, externalCreditAmount, isCreditReadOnly]);

  const ensureInitialSplit = useCallback(() => {
    if (isDebtOnly) {
      setSplits([]);
      return;
    }

    setSplits((current) => {
      if (current.length === 0) {
        const id = `split-${nextIdRef.current++}`;
        const outstanding = calculateOutstandingForSplit(null, current, totalDue, creditAmount);
        return [buildSplit(id, defaultMethodId, outstanding)];
      }

      if (defaultMethodId != null && current[0].methodId == null) {
        const [first, ...rest] = current;
        return [{ ...first, methodId: defaultMethodId }, ...rest];
      }
      return current;
    });
  }, [creditAmount, defaultMethodId, isDebtOnly, totalDue]);

  useEffect(() => {
    ensureInitialSplit();
  }, [ensureInitialSplit]);

  useEffect(() => {
    if (isDebtOnly) {
      return;
    }
    setSplits((current) => {
      if (current.length !== 1) {
        return current;
      }
      const [first] = current;
      const outstanding = calculateOutstandingForSplit(first.id, current, totalDue, creditAmount);
      if (Math.abs(first.amount - outstanding) < 0.01) {
        return current;
      }
      return [{ ...first, amount: outstanding }];
    });
  }, [totalDue, creditAmount, isDebtOnly]);

  const addSplit = useCallback((methodId?: number | null) => {
    if (isDebtOnly) {
      return;
    }
    setSplits((current) => {
      const id = `split-${nextIdRef.current++}`;
      const outstanding = calculateOutstandingForSplit(null, current, totalDue, creditAmount);
      const nextSplit = buildSplit(id, methodId ?? defaultMethodId, outstanding);
      return [...current, nextSplit];
    });
  }, [creditAmount, defaultMethodId, isDebtOnly, totalDue]);

  const removeSplit = useCallback((id: string) => {
    setSplits((current) => current.filter((split) => split.id !== id));
  }, []);

  const updateSplitMethod = useCallback((id: string, methodId: number | null) => {
    setSplits((current) =>
      current.map((split) => (split.id === id ? { ...split, methodId } : split)),
    );
  }, []);

  const updateSplitAmount = useCallback((id: string, amount: number) => {
    if (Number.isNaN(amount) || amount < 0) {
      amount = 0;
    }
    const safeAmount = clampCurrency(amount);
    setSplits((current) =>
      current.map((split) => (split.id === id ? { ...split, amount: safeAmount } : split)),
    );
  }, []);

  const assignTotalToPrimary = useCallback(() => {
    if (isDebtOnly) {
      return;
    }
    setSplits((current) => {
      if (current.length === 0) {
        const id = `split-${nextIdRef.current++}`;
        const outstanding = calculateOutstandingForSplit(null, current, totalDue, creditAmount);
        return [buildSplit(id, defaultMethodId, outstanding)];
      }
      const [first, ...rest] = current;
      const outstanding = calculateOutstandingForSplit(first.id, current, totalDue, creditAmount);
      return [{ ...first, amount: outstanding }, ...rest];
    });
  }, [creditAmount, defaultMethodId, isDebtOnly, totalDue]);

  const fillSplitWithRemaining = useCallback(
    (id: string) => {
      if (isDebtOnly) {
        return;
      }
      setSplits((current) => {
        const outstanding = calculateOutstandingForSplit(id, current, totalDue, creditAmount);
        return current.map((split) => (split.id === id ? { ...split, amount: outstanding } : split));
      });
    },
    [creditAmount, isDebtOnly, totalDue],
  );

  const sanitizedSplits = useMemo(
    () =>
      splits
        .map((split) => ({ ...split, amount: clampCurrency(Math.max(split.amount, 0)) }))
        .filter((split) => split.amount > 0),
    [splits],
  );

  const splitsTotal = useMemo(
    () => sanitizedSplits.reduce((accumulator, split) => accumulator + split.amount, 0),
    [sanitizedSplits],
  );

  const outstandingBeforeCredit = useMemo(() => {
    if (isDebtOnly) {
      return clampCurrency(Math.max(totalDue, 0));
    }
    return clampCurrency(Math.max(totalDue - splitsTotal, 0));
  }, [isDebtOnly, totalDue, splitsTotal]);

  const totalTendered = isDebtOnly ? 0 : clampCurrency(splitsTotal + creditAmount);
  const outstanding = isDebtOnly
    ? clampCurrency(Math.max(totalDue, 0))
    : clampCurrency(Math.max(totalDue - (splitsTotal + creditAmount), 0));
  const changeDue = isDebtOnly
    ? 0
    : clampCurrency(Math.max(splitsTotal + creditAmount - totalDue, 0));

  const balanceApplied = useMemo(() => {
    if (typeof balanceAdjustment === 'number' && Number.isFinite(balanceAdjustment)) {
      return normalizeCurrency(balanceAdjustment, { allowNegative: true });
    }
    return clampCurrency(creditAmount);
  }, [balanceAdjustment, creditAmount]);

  const balanceDebtPortion = balanceApplied < 0 ? clampCurrency(Math.abs(balanceApplied)) : 0;
  const balanceCreditPortion =
    balanceApplied > 0 ? clampCurrency(balanceApplied) : clampCurrency(creditAmount);

  const warnings: PaymentWarning[] = useMemo(() => {
    if (isDebtOnly) {
      return [
        {
          type: 'debt',
          amount: outstanding,
        },
      ];
    }
    const items: PaymentWarning[] = [];
    if (outstanding > 0.009) {
      items.push({ type: 'underpayment', amount: outstanding });
    }
    if (changeDue > 0.009) {
      items.push({ type: 'overpayment', amount: changeDue });
    }
    return items;
  }, [changeDue, isDebtOnly, outstanding]);

  const useMaxCredit = useCallback(() => {
    if (isDebtOnly || isCreditReadOnly) {
      return;
    }
    const amount = Math.min(creditCap, outstandingBeforeCredit);
    applyCreditAmount(amount);
  }, [applyCreditAmount, creditCap, isCreditReadOnly, isDebtOnly, outstandingBeforeCredit]);

  const computeOutstandingForSplit = useCallback(
    (id: string) => {
      if (isDebtOnly) {
        return 0;
      }
      return calculateOutstandingForSplit(id, splits, totalDue, creditAmount);
    },
    [creditAmount, isDebtOnly, splits, totalDue],
  );

  const summary = useMemo<PosPaymentSummary>(() => {
    if (isDebtOnly) {
      const note = debtNote.trim() || null;
      return {
        splits: [],
        creditApplied: 0,
        balanceApplied: 0,
        debtApplied: clampCurrency(Math.max(totalDue, 0)),
        registerDebt: true,
        debtNote: note,
        totalTendered: 0,
        outstanding: clampCurrency(Math.max(totalDue, 0)),
        changeDue: 0,
      };
    }

    const note = debtNote.trim() || null;
    return {
      splits: sanitizedSplits,
      creditApplied: balanceCreditPortion,
      balanceApplied,
      debtApplied: balanceDebtPortion,
      registerDebt: outstanding > 0.009,
      debtNote: note,
      totalTendered,
      outstanding,
      changeDue,
    };
  }, [
    balanceApplied,
    balanceCreditPortion,
    balanceDebtPortion,
    changeDue,
    debtNote,
    isDebtOnly,
    outstanding,
    sanitizedSplits,
    totalDue,
    totalTendered,
  ]);

  const totals = useMemo<PosPaymentsTotals>(
    () => ({
      totalTendered,
      outstanding,
      changeDue,
      outstandingBeforeCredit,
    }),
    [changeDue, outstanding, outstandingBeforeCredit, totalTendered],
  );

  useEffect(() => {
    if (!onPaymentsChange) {
      return;
    }
    onPaymentsChange({
      summary,
      totals,
      warnings,
      splits: sanitizedSplits,
      creditAmount,
      availableCredit,
      isDebtOnly,
      balanceApplied,
    });
  }, [
    availableCredit,
    balanceApplied,
    creditAmount,
    isDebtOnly,
    onPaymentsChange,
    sanitizedSplits,
    summary,
    totals,
    warnings,
  ]);

  const buildSummary = useCallback((): PosPaymentSummary => {
    const base = summary;
    return {
      ...base,
      splits: base.splits.map((split) => ({ ...split })),
    };
  }, [summary]);

  const reset = useCallback(() => {
    setSplits([]);
    nextIdRef.current = 1;
    if (isCreditReadOnly) {
      setCreditAmount(0);
      setCreditDraft('');
      setCreditManuallySet(false);
    } else {
      resetCredit();
    }
    setDebtOnly(false);
    setDebtNote('');
  }, [isCreditReadOnly, resetCredit]);

  return {
    paymentMethods,
    isLoading,
    splits,
    addSplit,
    removeSplit,
    updateSplitMethod,
    updateSplitAmount,
    fillSplitWithRemaining,
    assignTotalToPrimary,
    computeOutstandingForSplit,
    creditAmount,
    creditDraft,
    setCreditDraft: setCreditDraftValue,
    applyCreditAmount,
    useMaxCredit,
    resetCredit,
    availableCredit,
    isDebtOnly,
    setDebtOnly,
    debtNote,
    setDebtNote,
    totals,
    warnings,
    summary,
    buildSummary,
    reset,
    isCreditReadOnly,
  };
};

export default usePosPayments;
